import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { Alert, Button, Form, Input, Popconfirm, Row, Col, Select, Switch, Tooltip, DatePicker, InputNumber } from "antd"
import { MinusCircleOutlined } from "@ant-design/icons"
import moment from "moment"
import { isEmpty } from "lodash"
import { roundHumanizedNumbers } from "../../../lib/utilities"

import { fetchlinkedServiceBillSelectOptions, fetchServiceBill, setLinkedServiceBillSelectOptions, selectLinkedServiceBillOptions, selectPreviousBill, selectAccount } from "../billsSlice"
import { selectRoles } from "../../userRoles/userRolesSlice"
import WarningChip from "./subcomponents/WarningChip"
import ServiceBillHistory from "./subcomponents/ServiceBillHistory"
import DateMask from "../../forms/components/DateMask"

const { Option } = Select
const { TextArea } = Input
const dateFormat = "MM/DD/YYYY"

export default function LinkedServiceForm({ bill, form, accountId, initialValue }) {
    const dispatch = useDispatch()
    const { billId } = useParams()
    const linkedServiceBillOptions = useSelector(selectLinkedServiceBillOptions)
    const [selectedServiceBill, setSelectedServiceBill] = useState(null)
    const previousBill = useSelector(selectPreviousBill)
    const account = useSelector(selectAccount)
    const roles = useSelector(selectRoles)

    // Keep these as plain derived values -- `roles` is [] on first render and populated
    // asynchronously by ConsoleLayout, so snapshotting them into state would freeze the
    // unlink control in its pre-roles state.
    const isLocked = !isEmpty(bill) && (bill.finalized || bill.paid)
    const canUnlink = !isLocked && roles?.includes("super_admin")

    // The ids currently attached to this bill, read straight from the form store rather than
    // mirrored into local state -- a mirror can only drift.
    const linkedIds = () => (form.getFieldValue("linked_service_bills_attributes") || []).map((row) => row.id)

    useEffect(() => {
        if (accountId) {
            dispatch(fetchlinkedServiceBillSelectOptions(accountId))
        } else {
            dispatch(setLinkedServiceBillSelectOptions([]))
        }
    }, [dispatch, accountId])

    const handleServiceBillSelect = (value) => {
        setSelectedServiceBill(value)
    }

    const addLinkedService = async () => {
        if (selectedServiceBill && !linkedIds().includes(selectedServiceBill)) {
            const response = await dispatch(fetchServiceBill(selectedServiceBill))
            // console.log(response)
            if (response.success && !isEmpty(response.service_bill)) {
                // Check if we already have linked_service_bill so that we append to existing list
                if (!isEmpty(form.getFieldValue("linked_service_bills_attributes"))) {
                    form.setFieldsValue({
                        linked_service_bills_attributes: [
                            ...form.getFieldValue("linked_service_bills_attributes"),
                            {
                                ...response.service_bill,
                                supply_bill_id: billId,
                                fiscal_month: response.service_bill.fiscal_month  != null ? moment(response.service_bill.fiscal_month) : '', // moment(response.service_bill.fiscal_month || new Date())  <= Leaving just incase it's needed again
                                next_read_date: response.service_bill.next_read_date != null ? moment(response.service_bill.next_read_date) : '', // moment(response.service_bill.next_read_date || new Date())
                                previous_read_date: moment(response.service_bill.previous_read_date || new Date()),
                                read_date: moment(response.service_bill.read_date || new Date()),
                                service_bill_usage_factors_attributes: response.service_bill.service_bill_usage_factors.map((service_bill_usage_factor) => {
                                    return {
                                        ...service_bill_usage_factor,
                                    }
                                }),
                                service_bill_entry_fields_attributes: response.service_bill.service_bill_entry_fields.map((service_bill_entry_field) => {
                                    return {
                                        ...service_bill_entry_field,
                                    }
                                }),
                                service_bill_additional_entry_fields_attributes: response.service_bill.service_bill_additional_entry_fields.map((service_bill_additional_entry_field) => {
                                    return {
                                        ...service_bill_additional_entry_field,
                                    }
                                }),
                            },
                        ],
                    })
                    // No linked_service_bills so we can just create the object
                } else {
                    form.setFieldsValue({
                        linked_service_bills_attributes: [
                            {
                                ...response.service_bill,
                                supply_bill_id: billId,
                                fiscal_month: response.service_bill.fiscal_month  != null ? moment(response.service_bill.fiscal_month) : '', // moment(response.service_bill.fiscal_month || new Date())  <= Leaving just incase it's needed again
                                next_read_date: response.service_bill.next_read_date != null ? moment(response.service_bill.next_read_date) : '', // moment(response.service_bill.next_read_date || new Date())
                                previous_read_date: moment(response.service_bill.previous_read_date || new Date()),
                                read_date: moment(response.service_bill.read_date || new Date()),
                                service_bill_usage_factors_attributes: response.service_bill.service_bill_usage_factors.map((service_bill_usage_factor) => {
                                    return {
                                        ...service_bill_usage_factor,
                                    }
                                }),
                                service_bill_entry_fields_attributes: response.service_bill.service_bill_entry_fields.map((service_bill_entry_field) => {
                                    return {
                                        ...service_bill_entry_field,
                                    }
                                }),
                                service_bill_additional_entry_fields_attributes: response.service_bill.service_bill_additional_entry_fields.map((service_bill_additional_entry_field) => {
                                    return {
                                        ...service_bill_additional_entry_field,
                                    }
                                }),
                            },
                        ],
                    })
                }

                setSelectedServiceBill(null)
            }
        }
    }

    // Rows that have never been saved carry no supply_bill_id and have always been removable.
    // Everything else is a link a super admin is allowed to undo while the bill is still open.
    const showRemoveButton = (index) => {
        return !form.getFieldValue("linked_service_bills_attributes")[index].supply_bill_id || canUnlink
    }

    // The API refuses to unlink a service bill whose OWNING billable bill is finalized or paid --
    // unlinking clears its supply charges, and those belong to that other bill. Mirror the refusal
    // here so the control explains itself instead of surfacing a 422 the user could not predict.
    //
    // Read from the server's copy, keyed by the stable fieldKey, rather than from the form row:
    // anything added to a row object ships back inside linked_service_bills_attributes, and these
    // two are read-only metadata that Rails has no writer for. Rows added this session have no
    // server copy and are always removable -- dropping one never reaches unlinked_service_bill_ids.
    const parentBillLocked = (field) => {
        const serverRow = billId ? bill.linked_service_bills?.[field.fieldKey] : null
        return !!(serverRow?.parent_bill_finalized || serverRow?.parent_bill_paid)
    }

    // Unlinking detaches the Service Bill; it must never delete it. The Service Bill belongs to
    // the billable account's own Bill and only gets borrowed here via service_bills.supply_bill_id.
    //
    // This is why we deliberately do NOT push a `_destroy: true` tombstone the way every other
    // nested-row form in this app does (ClientForm, UtilityServiceForm, ServiceForm,
    // SiteSurveysContactsForm). The Bill model declares
    // `accepts_nested_attributes_for :linked_service_bills, allow_destroy: true`, so a tombstone
    // would delete the billable account's record and every charge on it. The unlink is expressed
    // instead by dropping the id from linked_service_bill_ids and naming it in
    // unlinked_service_bill_ids -- see submit() in BillForm.jsx.
    const removeLinkedService = (removeFnc, index) => {
        const row = form.getFieldValue("linked_service_bills_attributes")[index]

        // The options endpoint only returns service bills with no supply_bill_id, so a row we
        // just freed will not be in the list. Put it back so it can be re-selected without a
        // reload. Harmless if the endpoint is later widened to include it.
        if (row?.id && !linkedServiceBillOptions.some((option) => option.id === row.id)) {
            dispatch(
                setLinkedServiceBillSelectOptions([
                    ...linkedServiceBillOptions,
                    { id: row.id, full_name: `${row.service_utility_service_name} - ${row.service_meter_number}` },
                ])
            )
        }

        removeFnc()
    }

    return (
        <>
            <Row gutter={24}>
                <Col span={8}>
                    {/* This Select sits outside the Form.List, so adding or removing a row does not
                        re-render it on its own. Subscribing to the list keeps the disabled options honest. */}
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.linked_service_bills_attributes !== curr.linked_service_bills_attributes}>
                        {() => (
                            <Select placeholder={"Select"} showArrow={true} value={selectedServiceBill} onChange={handleServiceBillSelect} style={{ width: "100%" }} disabled={isLocked}>
                                {linkedServiceBillOptions.map((o) => (
                                    <Option key={o.id} value={o.id} disabled={linkedIds().includes(o.id)}>
                                        {o.full_name}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    </Form.Item>
                </Col>

                <Col span={8}>
                    <Button type="primary" onClick={addLinkedService} disabled={isLocked}>
                        Add Linked Service Bill
                    </Button>
                </Col>
            </Row>

            {/* remove this lol */}
            <br />
            <Form.List name="linked_service_bills_attributes" initialValue={initialValue}>
                {(fields, { remove }, {}) => (
                    <>
                        {fields.map((field, index) => {
                            let meterText
                            // NOTE: field.name is the positional index; field.fieldKey is a stable id from
                            // rc-field-form's keyManager. Live form values must be read by field.name -- reading
                            // them by fieldKey walks off the end of the array once a row has been removed.
                            const formServiceBill = form.getFieldValue("linked_service_bills_attributes")[field.name]

                            try {
                                const prevServiceBill =
                                    !isEmpty(previousBill) && previousBill.service_bills.filter((sb) => sb.service_id === formServiceBill.service_id)[0]

                                meterText = (
                                    <div>
                                        {`Service/Meter ${formServiceBill?.service_utility_service_name} - ${formServiceBill?.service_meter_number}`}

                                        {
                                            <span style={{ float: "right" }}>
                                                {prevServiceBill?.usage
                                                    ? `Previous Usage Was ${roundHumanizedNumbers(prevServiceBill?.usage)} ${prevServiceBill?.unit_symbol} From ${moment(
                                                          prevServiceBill?.read_date
                                                      ).format("L")} Reading of ${prevServiceBill?.meter_reading}`
                                                    : "No Record Of Usage From Previous Bill For This Service"}
                                            </span>
                                        }
                                    </div>
                                )
                            } catch (e) {
                                console.warn("Could not build meter text for linked service bill: ", e)
                            }
                            return (
                                <React.Fragment key={field.key}>
                                    <Alert
                                        message={meterText || "ERROR"}
                                        type="info"
                                        style={{ marginBottom: 12 }}
                                        action={
                                            showRemoveButton(index) &&
                                            (parentBillLocked(field) ? (
                                                // A disabled antd Button swallows its own mouse events, so the Tooltip
                                                // has to hang off a live wrapper or it never opens.
                                                <Tooltip title="This service bill belongs to a finalized or paid bill and cannot be unlinked.">
                                                    <span style={{ display: "inline-block", cursor: "not-allowed" }}>
                                                        <Button danger size="small" disabled icon={<MinusCircleOutlined />} style={{ pointerEvents: "none" }}>
                                                            Unlink
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                <Popconfirm
                                                    title="Unlink this service bill? Its supply charges will be cleared, and any unsaved changes to it will be discarded."
                                                    okText="Unlink"
                                                    cancelText="Cancel"
                                                    onConfirm={() => removeLinkedService(() => remove(field.name), index)}>
                                                    <Button danger size="small" icon={<MinusCircleOutlined />}>
                                                        Unlink
                                                    </Button>
                                                </Popconfirm>
                                            ))
                                        }
                                    />

                                    <Form.Item {...field} name={[field.name, "service_id"]} fieldKey={[field.fieldKey, "service_id"]} hidden={true} />

                                    <Row gutter={24}>
                                        <Col span={6}>
                                            <Row gutter={24}>
                                                <Col span={24}>
                                                    {/* Flags come from the ORIGINAL server array, so these lookups are keyed by
                                                        field.fieldKey (a stable per-row id), not by position. Do not "normalize"
                                                        them to field.name -- that would misattribute flags after a removal. */}
                                                    <WarningChip flagKey="previous_read_date_flag" message={billId ? bill.linked_service_bills[field.fieldKey]?.flags?.previous_read_date_flag : ""} />
                                                    <Form.Item
                                                        {...field}
                                                        label="Prev Read Date"
                                                        name={[field.name, "previous_read_date"]}
                                                        fieldKey={[field.fieldKey, "previous_read_date"]}
                                                        rules={[{ required: true, message: "Required" }]}>
                                                        <DatePicker format={dateFormat} style={{ width: "100%" }} disabled={isLocked} />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={24}>
                                                    <Form.Item
                                                        {...field}
                                                        label="Read Date"
                                                        name={[field.name, "read_date"]}
                                                        fieldKey={[field.fieldKey, "read_date"]}
                                                        rules={[{ required: true, message: "Required" }]}>
                                                        <DatePicker format={dateFormat} style={{ width: "100%" }} disabled={isLocked} />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={24}>
                                                    <Form.Item
                                                        {...field}
                                                        label="Next Read Date"
                                                        name={[field.name, "next_read_date"]}
                                                        fieldKey={[field.fieldKey, "next_read_date"]}
                                                        rules={[{ required: false, message: "Required" }]}>
                                                        <DatePicker format={dateFormat} style={{ width: "100%" }} disabled={isLocked} />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={24}>
                                                    <Form.Item
                                                        {...field}
                                                        label="Fiscal Month"
                                                        name={[field.name, "fiscal_month"]}
                                                        fieldKey={[field.fieldKey, "fiscal_month"]}
                                                        rules={[{ required: false, message: "Required" }]}>
                                                        <DatePicker format={dateFormat} style={{ width: "100%" }} disabled={isLocked} />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={24}>
                                                    <WarningChip flagKey="number_of_days_flag" message={billId ? bill.linked_service_bills[field.fieldKey]?.flags?.number_of_days_flag : ""} />
                                                    <Form.Item
                                                        {...field}
                                                        label="# of Days"
                                                        name={[field.name, "number_of_days"]}
                                                        fieldKey={[field.fieldKey, "number_of_days"]}
                                                        rules={[{ required: false, message: "Required" }]}>
                                                        <Input disabled={isLocked} />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </Col>

                                        <Col span={6}>
                                            <Row gutter={24}>
                                                <Col span={24}>
                                                    <WarningChip flagKey="previous_reading_flag" message={billId ? bill.linked_service_bills[field.fieldKey]?.flags?.previous_reading_flag : ""} />
                                                    <Form.Item {...field} label="Prev Reading" name={[field.name, "previous_reading"]} fieldKey={[field.fieldKey, "previous_reading"]}>
                                                        <InputNumber disabled={isLocked} style={{ width: "100%" }} precision={2} />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={24}>
                                                    <Form.Item {...field} label="Meter Reading" name={[field.name, "meter_reading"]} fieldKey={[field.fieldKey, "meter_reading"]}>
                                                        <Input disabled={isLocked}  style={{ width: "100%" }} precision={2}  />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={24}>
                                                    <WarningChip flagKey="multiplier_flag" message={billId ? bill.linked_service_bills[field.fieldKey]?.flags?.multiplier_flag : ""} />
                                                    <Form.Item {...field} label="multiplier" name={[field.name, "multiplier"]} fieldKey={[field.fieldKey, "multiplier"]}>
                                                        <Input disabled={isLocked}  style={{ width: "100%" }} precision={3} />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={24}>
                                                    <WarningChip flagKey="usage_flag" message={billId ? bill.linked_service_bills[field.fieldKey]?.flags?.usage_flag : ""} />
                                                    <Form.Item {...field} label="Usage" name={[field.name, "usage"]} fieldKey={[field.fieldKey, "usage"]} rules={[{ required: true, message: "Required" }]}>
                                                        <Input disabled={isLocked}  style={{ width: "100%" }} precision={3} />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={12}>
                                                    <Form.Item {...field} label="Unit" name={[field.name, "unit_id"]} fieldKey={[field.fieldKey, "unit_id"]}>
                                                        <Select showArrow={true} disabled={isLocked}>
                                                            {form.getFieldValue("linked_service_bills_attributes")[index].unit_options.map((o) => (
                                                                <Option key={o.id} value={o.id}>
                                                                    {o.symbol}
                                                                </Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                </Col>

                                                <Col span={12}>
                                                    <WarningChip flagKey="estimated_flag" message={billId ? bill.linked_service_bills[field.fieldKey]?.flags?.estimated_flag : ""} />
                                                    <Form.Item {...field} label="estimated" name={[field.name, "estimated"]} valuePropName="checked" fieldKey={[field.fieldKey, "estimated"]}>
                                                        <Switch disabled={isLocked} />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </Col>

                                        <Col span={6}>
                                            <Col span={24}>
                                                <Form.Item noStyle={true}>
                                                    <Form.List name={[field.name, "service_bill_entry_fields_attributes"]}>
                                                        {(service_bill_entry_fields_attributes, {}) => {
                                                            return (
                                                                <div>
                                                                    {service_bill_entry_fields_attributes.map((entryField, key2) => (
                                                                        <Form.Item
                                                                            {...entryField}
                                                                            label={form.getFieldValue("linked_service_bills_attributes")[index].service_bill_entry_fields_attributes[key2].name}
                                                                            name={[entryField.name, "value"]}
                                                                            fieldKey={[entryField.fieldKey, "value"]}
                                                                            key={key2}
                                                                            rules={[
                                                                                {
                                                                                    required:
                                                                                        form.getFieldValue("linked_service_bills_attributes")[index].service_bill_entry_fields_attributes[key2].name ===
                                                                                        "Supply Charges",
                                                                                    message: "Supply Charges are required",
                                                                                },
                                                                            ]}>
                                                                            <InputNumber disabled={isLocked} style={{ width: "100%" }} precision={2} />
                                                                        </Form.Item>
                                                                    ))}
                                                                </div>
                                                            )
                                                        }}
                                                    </Form.List>
                                                </Form.Item>
                                            </Col>
                                        </Col>

                                        <Col span={6}>
                                            <Row gutter={24}>
                                                <Col span={24} style={{ marginBottom: "0" }}>
                                                    <Form.Item noStyle={true}>
                                                        <Form.List name={[field.name, "service_bill_usage_factors_attributes"]}>
                                                            {(service_bill_usage_factors_attributes, {}) => {
                                                                return (
                                                                    <div>
                                                                        {service_bill_usage_factors_attributes.map((usageFactor, key2) => (
                                                                            <Form.Item
                                                                                {...usageFactor}
                                                                                label={form.getFieldValue("linked_service_bills_attributes")[index].service_bill_usage_factors_attributes[key2].name}
                                                                                name={[usageFactor.name, "value"]}
                                                                                fieldKey={[usageFactor.fieldKey, "value"]}
                                                                                key={key2}
                                                                                rules={[{ required: true, message: "Required" }]}>
                                                                                <InputNumber style={{ width: "100%" }} precision={4} />
                                                                            </Form.Item>
                                                                        ))}
                                                                    </div>
                                                                )
                                                            }}
                                                        </Form.List>
                                                    </Form.Item>
                                                </Col>

                                                <Col span={24}>
                                                    <Form.Item noStyle={true}>
                                                        <Form.List name={[field.name, "service_bill_additional_entry_fields_attributes"]}>
                                                            {(service_bill_additional_entry_fields_attributes, {}) => {
                                                                return (
                                                                    <div>
                                                                        {service_bill_additional_entry_fields_attributes.map((additionalEntryField, key2) => (
                                                                            <Form.Item
                                                                                {...additionalEntryField}
                                                                                label={
                                                                                    form.getFieldValue("linked_service_bills_attributes")[index].service_bill_additional_entry_fields_attributes[key2].name
                                                                                }
                                                                                name={[additionalEntryField.name, "value"]}
                                                                                fieldKey={[additionalEntryField.fieldKey, "value"]}
                                                                                key={key2}
                                                                                rules={[{ required: true, message: "Required" }]}>
                                                                                <InputNumber disabled={isLocked} style={{ width: "100%" }} precision={4} />
                                                                            </Form.Item>
                                                                        ))}
                                                                    </div>
                                                                )
                                                            }}
                                                        </Form.List>
                                                    </Form.Item>
                                                </Col>
                                                <Col span={24}>
                                                    <Form.Item
                                                        {...field}
                                                        label="Notes"
                                                        name={[field.name, "notes"]}
                                                        fieldKey={[field.fieldKey, "notes"]}>
                                                        <TextArea
                                                            style={{ width: "100%" }}
                                                            rows="3"
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>

                                    <ServiceBillHistory serviceId={form.getFieldValue("linked_service_bills_attributes")[index].service_id} />
                                </React.Fragment>
                            )
                        })}
                    </>
                )}
            </Form.List>
        </>
    )
}
